// One job: the page and its home card.
import {
  billingWords as billingLabel,
  homeWords,
  type JobDetail,
  type LineRow,
  type PriceList,
  type PropertyRow,
} from "@steward/shared";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useClient, useJob, useJobCosts, useSettings, useUpdateJob } from "../../api/hooks";
import { useInvoices, usePayments } from "../../api/money";
import { useSession } from "../../app/session";
import { clock, minutesWords, moneyExact, shortDate, timeRange, todayIn } from "../../components/format";
import { Icon } from "../../components/Icon";
import { NextStep } from "../../components/next";
import { Menu, useToast } from "../../components/overlays";
import { Banner, Button, Card, Chip, Details, IconButton, LoadError, Page, Skeleton, StatusLabel } from "../../components/parts";
import { PhoneRecord } from "../../components/PhoneRecord";
import { PlanOfferSend } from "../../components/PlanOffer";
import { usePhone } from "../../components/usePhone";
import { CrewChips, errText, LineMore, LinesTable, NotesCard } from "../../components/work";
import { InvoiceStatus } from "../invoices/common";
import { cardLine, JobBilling, JobExpenses, JobLabor, JobProfit, JobTimeLog } from "../JobCosts";
import { HomeSizeEdit, PricedNote, RepriceLine } from "./Price";
import { ConfirmForThem, JobVisits } from "./Visits";

export function JobPage({ id }: { id: string }) {
  const { company, can, me } = useSession();
  const navigate = useNavigate();
  const toast = useToast();
  const job = useJob(id);
  const update = useUpdateJob();
  const prices = useSettings(company.id).data?.settings.prices;
  const [repricing, setRepricing] = useState<LineRow | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [lang, setLang] = useState<"en" | "es">(me.locale === "es" ? "es" : "en");
  const phone = usePhone();
  // The phone page has Call, Text and Email up top, from the client. Both pages show the home's size over the line items.
  const client = useClient(job.data?.clientId ?? "", !!job.data && can("client.read"));
  // The phone page's Invoices and Payments, like Jobber's job.
  const [jobTab, setJobTab] = useState<"job" | "notes">("job");
  const onPhoneBills = phone && !!job.data && can("invoice.read");
  const jobInvoices = useInvoices(company.id, { state: "all", jobId: job.data?.id ?? "" }, onPhoneBills);
  const jobPayments = usePayments(company.id, { jobId: job.data?.id ?? "" }, onPhoneBills);
  // Expenses, Labor and Profit (Labor and Profit come back only for people who see payroll).
  const costs = useJobCosts(job.data?.id ?? "", !!job.data && can("job.read"));
  const j = job.data;
  if (job.error)
    return (
      <Page title="Job">
        <LoadError error={job.error} retry={() => void job.refetch()} />
      </Page>
    );
  if (!j)
    return (
      <Page title={<span className="skeleton inline wide" />}>
        <Skeleton rows={6} />
      </Page>
    );
  const editable = can("job.update");
  const repeating = j.frequency !== "one-time";
  const setActive = (active: boolean) =>
    update.mutate(
      { id: j.id, patch: { active } },
      {
        onSuccess: () =>
          toast(active ? "Job started again" : "Job ended. No more visits get planned.", {
            undo: () => update.mutate({ id: j.id, patch: { active: !active } }),
          }),
        onError: (e) => toast(errText(e), { kind: "error" }),
      },
    );
  const status = j.confirm ? (
    <StatusLabel status="warning">Not confirmed</StatusLabel>
  ) : j.waitingOk ? (
    <StatusLabel status="warning">Waiting for your OK</StatusLabel>
  ) : j.completedOn ? (
    <StatusLabel status="success">Completed {shortDate(j.completedOn)}</StatusLabel>
  ) : j.active ? null : (
    <StatusLabel status="inactive">Ended {shortDate(j.endsOn)}</StatusLabel>
  );
  const editBtn = editable ? (
    <Button icon="edit" onClick={() => void navigate({ to: "/jobs/$id/edit", params: { id: j.id } })}>
      Edit job
    </Button>
  ) : null;
  const scheduleBtn = can("schedule.read") ? (
    <Button type="secondary" icon="schedule" onClick={() => void navigate({ to: "/schedule" })}>
      Schedule
    </Button>
  ) : null;
  const moreMenu = editable ? (
    <Menu align="right" button={({ toggle }) => <IconButton icon="more" label="More" onClick={toggle} />}>
      {(close) => (
        <>
          {j.confirm ? (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                close();
                setConfirming(true);
              }}
            >
              Confirm for them
            </button>
          ) : null}
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              close();
              setActive(!j.active);
            }}
          >
            {j.active ? (repeating ? "End this job" : "Cancel this job") : "Start this job again"}
          </button>
        </>
      )}
    </Menu>
  ) : null;
  const banners = (
    <>
      <NextStep freshKey={`job:${j.id}`} title="The job is made. Its visits are on the schedule.">
        <Button type="secondary" size="small" icon="schedule" onClick={() => void navigate({ to: "/schedule" })}>
          Open the schedule
        </Button>
      </NextStep>
      {j.confirm ? (
        <Banner
          type="notice"
          action={
            <div className="row">
              <Button
                type="secondary"
                size="small"
                onClick={() => void navigate({ to: "/quotes/$id", params: { id: j.confirm?.quoteId ?? "" } })}
              >
                {j.confirm.sentTo ? "See the link" : "Send the link"}
              </Button>
              {editable ? (
                <Button size="small" onClick={() => setConfirming(true)}>
                  Confirm for them
                </Button>
              ) : null}
            </div>
          }
        >
          <b>Not confirmed yet.</b>{" "}
          {j.confirm.sentTo
            ? `It is on the schedule. ${j.clientName} got a link at ${j.confirm.sentTo} to confirm and sign.`
            : `It is on the schedule. ${j.clientName} has no phone or email. Send the confirm link from the quote.`}
        </Banner>
      ) : j.waitingOk ? (
        <Banner
          type="warning"
          action={
            editable ? (
              <Button size="small" onClick={() => setActive(true)}>
                Book it
              </Button>
            ) : null
          }
        >
          <b>The client asked for this visit in My Trades.</b> It is not on the schedule yet. {j.waitingOk.join(" ")}
        </Banner>
      ) : null}
      {j.officeNotes ? (
        <Banner type="notice">
          <b>Office note.</b> {j.officeNotes}
        </Banner>
      ) : null}
      {confirming && j.confirm ? <ConfirmForThem job={j} onClose={() => setConfirming(false)} /> : null}
    </>
  );
  // Home cleaning only for now (owner, 2026-11-02: businesses wait until it is refined).
  const homeClean = company.trade === "cleaning" && client.data?.kind === "home";
  const place = client.data?.properties.find((p) => p.id === j.propertyId) ?? null;
  // A job not priced from the price list prices its first line from the home's size, to offer a repeat fast.
  const unpriced = !j.lines.some((l) => l.answers);
  const canPrice = editable && Boolean(prices?.services.length);
  const linesCard = (
    <>
      <Card title="Line items" flush>
        {homeClean ? (
          <JobHome
            job={j}
            prices={prices}
            place={place}
            clientId={j.clientId}
            priced={j.lines.find((l) => l.basis)?.basis ?? null}
            editable={editable && can("client.update")}
            onPrice={canPrice && unpriced && j.lines[0] ? () => setRepricing(j.lines[0] ?? null) : null}
            offer={!repeating && can("quote.update")}
          />
        ) : null}
        <LinesTable
          lines={j.lines}
          footer={<span className="muted">Each visit</span>}
          onOpen={(l) =>
            canPrice && (l.answers || (homeClean && unpriced && l.id === j.lines[0]?.id)) ? () => setRepricing(l) : null
          }
          below={(l) => (
            <>
              <PricedNote line={l} list={prices} />
              <LineMore line={l} basis={!(l.answers && prices)} />
            </>
          )}
        />
      </Card>
      {repricing && prices ? (
        <RepriceLine job={j} line={repricing} list={prices} place={place} onClose={() => setRepricing(null)} />
      ) : null}
    </>
  );
  const visitsCard = <JobVisits job={j} today={todayIn(company.timezone)} />;
  const expensesCard = <JobExpenses j={j} costs={costs} />;
  const laborCard = costs.data?.labor ? <JobLabor costs={costs.data} /> : null;
  const timeCard = <JobTimeLog j={j} costs={costs} />;
  const profitCard = costs.data?.profit ? <JobProfit costs={costs.data} /> : null;
  const notesCard = <NotesCard on="jobs" id={j.id} notes={j.notesList} editable={editable} />;
  const detailsCard = (
    <Card title="Details">
      <Details
        rows={[
          // A repeating job shows how it repeats, its first and last visit and the checklist on Scheduled visits.
          ...(repeating
            ? []
            : ([
                ["Repeats", j.frequencyLabel],
                ["Date", shortDate(j.startDate)],
              ] as [string, string][])),
          ["Time", j.arriveFrom ? `Arrive ${timeRange(j.arriveFrom, j.arriveTo)}` : clock(j.startTime)],
          ["Takes", j.hours ? minutesWords(j.hours * 60) : null],
          ["Where", j.address],
          ["Crew", <CrewChips key="crew" crew={j.crew} />],
          ["Billing", billingLabel(j.billing)],
          ...(repeating ? [] : ([["Checklist", j.checklist?.name ?? null]] as [string, string | null][])),
        ]}
      />
    </Card>
  );
  const crewNotesCard = (
    <Card
      title="Crew notes"
      action={
        j.crewNotesEs ? (
          <div className="chips">
            <Chip pressed={lang === "en"} onClick={() => setLang("en")}>
              English
            </Chip>
            <Chip pressed={lang === "es"} onClick={() => setLang("es")}>
              Español
            </Chip>
          </div>
        ) : null
      }
    >
      {(lang === "es" ? j.crewNotesEs : j.crewNotes) ?? <span className="muted">No notes for the crew.</span>}
      {j.crewNotes && !j.crewNotesEs ? (
        <p className="muted small">No Spanish notes yet. Spanish speakers see the English.</p>
      ) : null}
    </Card>
  );

  if (phone) {
    // Like Jobber's job on a phone (owner shots, 2026-10-30): Job #14 for the client for the total, the job's name, the
    // address, the start, Go to Visits, then Job and Notes tabs. The Job tab runs Instructions, Line items, totals,
    // Schedule, Visits, Overview, Invoicing, Invoices and Payments, flush with lines between.
    const totalCents = j.lines.reduce((s, l) => s + l.amountCents, 0);
    const goVisits = () => {
      setJobTab("job");
      requestAnimationFrame(() => document.getElementById("job-visits")?.scrollIntoView({ behavior: "smooth" }));
    };
    const phoneMore = (
      <Menu align="left" button={({ toggle }) => <IconButton icon="more" label="More" onClick={toggle} />}>
        {(close) => (
          <>
            {editable && j.confirm ? (
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  close();
                  setConfirming(true);
                }}
              >
                Confirm for them
              </button>
            ) : null}
            {editable ? (
              <button
                type="button"
                role="menuitem"
                onClick={() => (close(), void navigate({ to: "/jobs/$id/edit", params: { id: j.id } }))}
              >
                Edit job
              </button>
            ) : null}
            {can("schedule.read") ? (
              <button type="button" role="menuitem" onClick={() => (close(), void navigate({ to: "/schedule" }))}>
                Open the schedule
              </button>
            ) : null}
            <button
              type="button"
              role="menuitem"
              onClick={() => (close(), void navigate({ to: "/clients/$id", params: { id: j.clientId } }))}
            >
              Open client
            </button>
            {editable ? (
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  close();
                  setActive(!j.active);
                }}
              >
                {j.active ? (repeating ? "End this job" : "Cancel this job") : "Start this job again"}
              </button>
            ) : null}
          </>
        )}
      </Menu>
    );
    const invoiceRows = (jobInvoices.data?.invoices ?? []).filter((i) => i.state !== "to_check");
    const paymentRows = (jobPayments.data?.payments ?? []).filter((p) => p.status !== "failed");
    const billingWords = j.billing === "visit" ? "When a visit is marked complete" : billingLabel(j.billing);
    return (
      <PhoneRecord
        back={{ label: "Back", go: () => void navigate({ to: "/jobs" }) }}
        kind="hammer"
        status={status ?? <StatusLabel status="success">Active</StatusLabel>}
        title={
          <>
            Job {j.number ? `#${String(j.number)} ` : ""}for {j.clientName}
            {totalCents ? ` for ${moneyExact(totalCents)}` : ""}
          </>
        }
        sub={<span className="prec-sub-muted">{j.name}</span>}
        phone={client.data?.phone ?? null}
        email={client.data?.email ?? null}
        facts={[
          ...(j.address
            ? [{ key: "where", icon: "directions" as const, main: j.address, sub: "Directions", directions: j.address }]
            : []),
          {
            key: "start",
            icon: "schedule" as const,
            main: j.startDate ? `${repeating ? "Start date" : "Date"} ${shortDate(j.startDate)}` : "No start date",
            sub: j.nextVisit ? `Next visit ${shortDate(j.nextVisit)}` : j.frequencyLabel,
          },
        ]}
        actions={
          <>
            <Button onClick={goVisits}>Go to Visits</Button>
            {phoneMore}
          </>
        }
        tabs={{
          tabs: [
            { key: "job", label: "Job" },
            { key: "notes", label: "Notes" },
          ],
          active: jobTab,
          onChange: (k) => setJobTab(k as typeof jobTab),
        }}
      >
        {banners}
        {jobTab === "notes" ? (
          notesCard
        ) : (
          <>
            {crewNotesCard}
            {linesCard}
            <section className="card prec-totals">
              <div>
                <span>Subtotal</span>
                <b className="num">{moneyExact(totalCents)}</b>
              </div>
              <div className="total">
                <span>Total</span>
                <span className="num">{moneyExact(totalCents)}</span>
              </div>
            </section>
            <Card title="Schedule">
              <dl className="prec-kv">
                <div>
                  <dt>{repeating ? "Start date" : "Date"}</dt>
                  <dd>{j.startDate ? shortDate(j.startDate) : "Not set"}</dd>
                </div>
                <div>
                  <dt>Arrival</dt>
                  <dd>{j.arriveFrom ? `Arrive ${timeRange(j.arriveFrom, j.arriveTo)}` : clock(j.startTime)}</dd>
                </div>
                <div>
                  <dt>Repeating</dt>
                  <dd>{j.frequencyLabel}</dd>
                </div>
                {repeating ? (
                  <div>
                    <dt>Ends</dt>
                    <dd>{j.endsOn ? shortDate(j.endsOn) : "Keeps going"}</dd>
                  </div>
                ) : null}
                {j.hours ? (
                  <div>
                    <dt>Takes</dt>
                    <dd>{minutesWords(j.hours * 60)}</dd>
                  </div>
                ) : null}
                <div>
                  <dt>Crew</dt>
                  <dd>
                    <CrewChips crew={j.crew} />
                  </dd>
                </div>
                {j.checklist ? (
                  <div>
                    <dt>Checklist</dt>
                    <dd>{j.checklist.name}</dd>
                  </div>
                ) : null}
              </dl>
            </Card>
            <div id="job-visits">{visitsCard}</div>
            <Card title="Overview" flush>
              <div className="prec-list">
                <div className="prec-row">
                  <span>
                    <b>{j.name}</b>
                  </span>
                </div>
                <div className="prec-row">
                  <span>
                    <span className="muted">Job number</span>
                    <b>{j.number ? `#${String(j.number)}` : "None yet"}</b>
                  </span>
                </div>
                <button
                  type="button"
                  className="prec-row"
                  onClick={() => void navigate({ to: "/clients/$id", params: { id: j.clientId } })}
                >
                  <Icon name="person" size={22} />
                  <span>
                    <b>{j.clientName}</b>
                    {j.address ? <span className="muted">{j.address}</span> : null}
                  </span>
                  <Icon name="chevronRight" size={20} />
                </button>
              </div>
            </Card>
            <Card title="Invoicing">
              <dl className="prec-kv">
                <div>
                  <dt>Type</dt>
                  <dd>{j.billing === "visit" ? "Visit based" : j.billing === "outside" ? "Not billed here" : "Monthly"}</dd>
                </div>
                <div>
                  <dt>Frequency</dt>
                  <dd>{billingWords}</dd>
                </div>
                <div>
                  <dt>Automatic payments</dt>
                  <dd>{j.autoCharge === "on" ? "On" : "Off"}</dd>
                </div>
                {j.card !== undefined ? (
                  <div>
                    <dt>Card on file</dt>
                    <dd>{cardLine(j.card, todayIn(company.timezone))}</dd>
                  </div>
                ) : null}
              </dl>
            </Card>
            {can("invoice.read") ? (
              <>
                <Card title="Invoices" flush>
                  {jobInvoices.isLoading ? (
                    <Skeleton rows={2} />
                  ) : invoiceRows.length ? (
                    <div className="prec-list">
                      {invoiceRows.map((i) => (
                        <button
                          key={i.id}
                          type="button"
                          className="prec-row"
                          onClick={() => void navigate({ to: "/invoices/$id", params: { id: i.id } })}
                        >
                          <Icon name="invoices" size={22} />
                          <span>
                            <b>{i.number ? `Invoice #${String(i.number)}` : "Invoice"}</b>
                            <span className="muted num">
                              {shortDate(i.issuedOn ?? i.firstServiceDate ?? "")} | {moneyExact(i.totalCents)}
                            </span>
                          </span>
                          <InvoiceStatus i={i} />
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="prec-empty">No invoices for this job yet.</p>
                  )}
                </Card>
                {paymentRows.length ? (
                  <Card title="Payments" flush>
                    <div className="prec-list">
                      {paymentRows.map((p) => (
                        <button
                          key={p.id}
                          type="button"
                          className="prec-row"
                          onClick={() => void navigate({ to: "/payments/$id", params: { id: p.id } })}
                        >
                          <span>
                            <b>Payment</b>
                            {p.tipCents > 0 ? <span className="muted num">Tip {moneyExact(p.tipCents)}</span> : null}
                          </span>
                          <span className="right num">-{moneyExact(p.amountCents)}</span>
                        </button>
                      ))}
                    </div>
                  </Card>
                ) : null}
              </>
            ) : null}
            {expensesCard}
            {timeCard}
            {laborCard}
            {profitCard}
          </>
        )}
      </PhoneRecord>
    );
  }

  return (
    <Page
      title={
        <>
          {j.name} <span className="muted num">#{j.number}</span>
        </>
      }
      titleMetaData={status}
      subtitle={
        <button type="button" className="link" onClick={() => void navigate({ to: "/clients/$id", params: { id: j.clientId } })}>
          {j.clientName}
        </button>
      }
      primaryAction={editBtn}
      secondaryAction={scheduleBtn}
      moreActions={moreMenu}
    >
      {banners}
      <div className="home-grid">
        <div className="stack">
          {linesCard}
          {visitsCard}
          {can("invoice.read") ? <JobBilling j={j} /> : null}
          {expensesCard}
          {timeCard}
          {laborCard}
          {notesCard}
        </div>
        <div className="stack">
          {detailsCard}
          {profitCard}
          {crewNotesCard}
        </div>
      </div>
    </Page>
  );
}

/**
 * Over a job's line items: the home's bedrooms, bathrooms, other rooms and square feet (owner, 2026-11-02: the crew ask
 * for it so they know what is part of the job and do not over clean, and the office offers a repeat fast from it). The
 * client sees the same size on their quote and invoice. A home with no size saved shows what the job was priced for.
 */
function JobHome({
  job,
  prices,
  place,
  clientId,
  priced,
  editable,
  onPrice,
  offer,
}: {
  job: JobDetail;
  prices: PriceList | undefined;
  place: PropertyRow | null;
  clientId: string;
  priced: string | null;
  editable: boolean;
  onPrice: (() => void) | null;
  /** A one-time job: Offer a plan texts them a link to start their own schedule (owner, 2026-11-02). */
  offer: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [offering, setOffering] = useState(false);
  const size = place ? homeWords(place) : null;
  const words = size ?? priced;
  if (!place && !words) return null;
  return (
    <div className="lines-home">
      <span className="what">
        <span className="muted">Home </span>
        {words ? <b>{words}</b> : <span className="muted">No size yet. Add it so the crew know what is part of the job.</span>}
      </span>
      <span className="acts">
        {editable && place ? (
          <Button type="tertiary" size="small" onClick={() => setEditing(true)}>
            {size ? "Change" : "Add size"}
          </Button>
        ) : null}
        {onPrice ? (
          <Button type="tertiary" size="small" onClick={onPrice}>
            Price a repeat
          </Button>
        ) : null}
        {offer && words ? (
          <Button type="secondary" size="small" onClick={() => setOffering(true)}>
            Offer a plan
          </Button>
        ) : null}
      </span>
      {editing && place ? (
        <HomeSizeEdit place={place} clientId={clientId} job={job} prices={prices} onClose={() => setEditing(false)} />
      ) : null}
      {offering ? <PlanOfferSend clientId={clientId} onClose={() => setOffering(false)} /> : null}
    </div>
  );
}
